import { useState } from "react";
import { Loader2, MapPin, Radar } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Small debug widget: enter lat/lng + radius → hit /api/retailers/nearby.
 * Shows the closest N outlets with distance_m.
 */
export default function NearbyRetailers() {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(1000);
  const [limit, setLimit] = useState(20);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const useMyLoc = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); },
      (err) => toast.error(err.message),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const find = async () => {
    if (lat === "" || lng === "") return toast.error("Enter lat / lng");
    setBusy(true);
    try {
      const { data } = await api.get("/retailers/nearby", {
        params: { lat: Number(lat), lng: Number(lng), radius_m: Number(radius), limit: Number(limit) },
      });
      setItems(data.items || []);
      toast.success(`Found ${data.items.length} nearby outlets within ${radius}m`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="retailers-nearby-widget">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Radar className="h-5 w-5 text-[#E5197F]" />
          <h3 className="font-semibold text-neutral-900">Test nearby outlets</h3>
          <span className="text-xs text-neutral-500">— GPS-driven query using $geoNear on the 2dsphere index</span>
        </div>
        <div className="grid grid-cols-[1fr_1fr_120px_100px_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Latitude</Label>
            <Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)}
                   placeholder="23.0225" data-testid="nearby-lat" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Longitude</Label>
            <Input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)}
                   placeholder="72.5714" data-testid="nearby-lng" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Radius (m)</Label>
            <Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="nearby-radius" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-neutral-500">Limit</Label>
            <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="nearby-limit" />
          </div>
          <Button variant="outline" onClick={useMyLoc} data-testid="nearby-mylocation">
            <MapPin className="h-4 w-4 mr-1" /> My location
          </Button>
          <Button onClick={find} disabled={busy} className="tulsi-gradient text-white font-semibold" data-testid="nearby-find">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}
          </Button>
        </div>
        {items.length > 0 && (
          <div className="rounded-xl border border-neutral-200 overflow-hidden text-xs" data-testid="nearby-results">
            <table className="w-full">
              <thead className="bg-neutral-50 text-neutral-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-right px-3 py-2">Distance</th>
                  <th className="text-left px-3 py-2">GPS</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-3 py-2 font-mono">{r.code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2 text-right">{Math.round(r.distance_m)} m</td>
                    <td className="px-3 py-2 font-mono text-neutral-500">
                      {r.gps_lat?.toFixed?.(4)}, {r.gps_lng?.toFixed?.(4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
